import ApiService from './ApiService';
// Stripe is initialized inline when needed
// import { loadStripe } from '@stripe/stripe-js';

export interface SubscriptionTier {
  id: number;
  name: string;
  displayName: string;
  price: number;
  currency: string;
  features: string[];
  likesPerDay: number;
  canSeeWhoLiked: boolean;
  canUseAdvancedFilters: boolean;
  monthlyBoosts: number;
  priority: number;
  isActive: boolean;
}

export interface Subscription {
  id: number;
  userId: number;
  tierId: number;
  status: string;
  stripeSubscriptionId?: string;
  currentPeriodStart?: string;
  currentPeriodEnd?: string;
  cancelAtPeriodEnd: boolean;
  metadata?: string | any;
  tier: SubscriptionTier;
}

export interface LikeQuota {
  remaining: number;
  limit: number;
  unlimited: boolean;
  tierName: string;
}

export interface Product {
  id: string;
  name: string;
  description: string;
  type: string;
  price: number;
  currency: string;
  duration?: number;
  multiplier?: number;
}

export class SubscriptionServiceClass {
  // Get all subscription tiers
  async getTiers(): Promise<SubscriptionTier[]> {
    const response = await ApiService.get('/subscriptions/tiers');
    return response.data;
  }

  // Get current user's subscription
  async getCurrentSubscription(): Promise<Subscription> {
    const response = await ApiService.get('/subscriptions/current');
    return response.data;
  }

  // Create checkout session for subscription upgrade
  async createSubscriptionCheckout(tierId: number): Promise<void> {
    const response = await ApiService.post('/subscriptions/checkout', { tierId });
    const { url } = response.data;

    // Redirect to Stripe Checkout
    if (url) {
      window.location.href = url;
    }
  }

  // Cancel subscription
  async cancelSubscription(): Promise<void> {
    await ApiService.post('/subscriptions/cancel');
  }

  // Reactivate subscription
  async reactivateSubscription(): Promise<void> {
    await ApiService.post('/subscriptions/reactivate');
  }

  // Get available products (boosts)
  async getProducts(): Promise<Product[]> {
    const response = await ApiService.get('/payments/products');
    return response.data;
  }

  // Create checkout for individual purchase
  async createPurchaseCheckout(productId: string): Promise<void> {
    const response = await ApiService.post('/payments/checkout', { productId });
    const { url } = response.data;

    // Redirect to Stripe Checkout
    if (url) {
      window.location.href = url;
    }
  }

  // Get like quota
  async getLikeQuota(): Promise<LikeQuota> {
    const response = await ApiService.get('/likes/quota');
    return response.data;
  }
}

const SubscriptionService = new SubscriptionServiceClass();
export default SubscriptionService;
